import React, { useState } from "react";
import { Checkbox, Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import {
  type ConnectionEditState,
  type ConnectionEditStateUpdater,
  type Status,
  type SourceStatusMapping,
} from "@onetasklist/shared";
import {
  useTrelloSourceSettingsText,
  useTrelloSourceSettingsViewModel,
} from "@onetasklist/shared/sources/trello";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";
import { StatusMappingSection } from "./components/StatusMappingSection";

interface TrelloSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
  boardStatuses: Status[];
}

export const TrelloSourceSettings: React.FC<TrelloSourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
  boardStatuses,
}) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useTrelloSourceSettingsText(t);
  const vm = useTrelloSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [boardQuery, setBoardQuery] = useState("");
  const [listsToLoadQuery, setListsToLoadQuery] = useState("");
  const [doneListQuery, setDoneListQuery] = useState("");
  const [labelsQuery, setLabelsQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  return (
    <>
      <SettingsSectionCard
        sectionOffset={sectionOffset}
        title={t(
          "board.connections.catalogue.sourceSettings.trello.sectionBoard",
        )}
      >
        <Field orientation="horizontal" label={text.boardLabelText}>
          <SourceSingleTagPicker
            selectedId={
              vm.selectedBoardId === vm.noneOptionValue
                ? undefined
                : vm.selectedBoardId
            }
            selectedLabel={vm.selectedBoardName}
            query={boardQuery}
            placeholder={text.boardPlaceholderText}
            options={vm.boards.map((board) => ({
              id: board.id,
              label: board.name || board.id,
            }))}
            onQueryChange={setBoardQuery}
            onSelect={vm.onSelectBoard}
            clearSelectionText={text.boardPlaceholderText}
            isLoading={!vm.boardsLoaded}
          />
        </Field>
      </SettingsSectionCard>

      {vm.selectedBoardId !== vm.noneOptionValue && (
        <SettingsSectionCard
          sectionOffset={sectionOffset + 1}
          title={t(
            "board.connections.catalogue.sourceSettings.trello.sectionLists",
          )}
        >
          <Field
            orientation="horizontal"
            label={text.listsToLoadLabelText}
            hint={text.listsToLoadHintText}
          >
            <SourceMultiTagPicker
              selectedIds={vm.selectedListsToLoadIds}
              query={listsToLoadQuery}
              placeholder={text.listsToLoadPlaceholderText}
              options={vm.lists.map((list) => ({
                id: list.id,
                label: list.name || list.id,
              }))}
              onQueryChange={setListsToLoadQuery}
              onSelectionChange={vm.onSelectListsToLoad}
              minQueryLength={0}
              typeToSearchText={text.listsToLoadHintText}
              isLoading={!vm.listsLoaded}
            />
          </Field>

          <Field
            orientation="horizontal"
            label={text.doneListLabelText}
            hint={text.doneListHintText}
            validationMessage={vm.statusReferenceErrorText || undefined}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedDoneListId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedDoneListId
              }
              selectedLabel={vm.selectedDoneListName}
              query={doneListQuery}
              placeholder={text.doneListPlaceholderText}
              options={vm.lists.map((list) => ({
                id: list.id,
                label: list.name || list.id,
              }))}
              onQueryChange={setDoneListQuery}
              onSelect={vm.onSelectDoneList}
              clearSelectionText={text.doneListPlaceholderText}
              isLoading={!vm.listsLoaded}
            />
          </Field>

          <Field
            orientation="horizontal"
            label={text.labelsFilterLabelText}
            hint={text.labelsFilterHintText}
          >
            <SourceMultiTagPicker
              selectedIds={vm.selectedLabelFilterIds}
              query={labelsQuery}
              placeholder={text.labelsFilterPlaceholderText}
              options={vm.labels.map((label) => ({
                id: label.id,
                label: label.name || label.id,
              }))}
              onQueryChange={setLabelsQuery}
              onSelectionChange={vm.onSelectLabelFilter}
              minQueryLength={0}
              typeToSearchText={text.labelsFilterHintText}
              isLoading={!vm.labelsLoaded}
            />
          </Field>

          <Field orientation="horizontal" label={text.assignedToMeLabelText}>
            <Checkbox
              checked={vm.assignedToMe}
              onChange={(_, data) =>
                vm.onToggleAssignedToMe(Boolean(data.checked))
              }
            />
          </Field>
        </SettingsSectionCard>
      )}

      {(!vm.boardsLoaded || !vm.listsLoaded || !vm.labelsLoaded) && (
        <Text>{text.loadingText}</Text>
      )}
      {vm.hasError && <Text>{text.loadErrorText}</Text>}

      {vm.selectedListsToLoadIds.length > 0 && (
        <StatusMappingSection
          sectionNumber={sectionOffset + 1}
          items={vm.lists
            .filter((l) => vm.selectedListsToLoadIds.includes(l.id))
            .map((l) => ({ id: l.id, name: l.name || l.id }))}
          mappings={
            ((editingConnection.configuration as Record<string, unknown>)
              ?.statusMappings as SourceStatusMapping[] | undefined) ?? []
          }
          boardStatuses={boardStatuses}
          onChange={(mappings) =>
            onUpdate({
              configuration: {
                ...(editingConnection.configuration as Record<string, unknown>),
                statusMappings: mappings,
              },
            })
          }
        />
      )}
    </>
  );
};
